import { Component, computed, inject, Input, OnDestroy } from '@angular/core';
import {
  PickListModule,
  PickListMoveToSourceEvent,
  PickListMoveToTargetEvent,
} from 'primeng/picklist';
import { Tag } from '../../../models/tag';
import { TagStore } from '../../../stores/tag/tag.state';
import { DividerModule } from 'primeng/divider';
import { SharedModule } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { TagFilterComponent } from '../tag-filter/tag-filter.component';
import { addById, removeByIds } from '../../../utility/array.utility';

/**
 * Pick list for tags backed by the `TagStore` selection.
 *
 * The store selection is the single source of truth, both lists are derived from it
 * (and from the current filter). Moves are applied as a diff, so selected tags
 * hidden by the filter are never lost.
 */
import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
@Component({
  selector: 'app-tag-pick-list',
  standalone: true,
  imports: [
    DividerModule,
    PickListModule,
    SharedModule,
    TooltipModule,
    TagFilterComponent,
    PickListHeaderComponent,
  ],
  templateUrl: './tag-pick-list.component.html',
  styleUrl: './tag-pick-list.component.scss',
})
export class TagPickListComponent implements OnDestroy {
  @Input() showSourceControls = false;
  /** The target list is always sorted by name, manual reordering has no effect. */
  @Input() showTargetControls = false;
  @Input() keepSelection = false;

  readonly tagStore = inject(TagStore);

  private readonly selectedIds = computed(
    () => new Set(this.tagStore.selection().map(tag => tag._id))
  );

  /** Filtered tags which are not selected yet. */
  readonly sourceList = computed(() => {
    const selected = this.selectedIds();
    return this.tagStore
      .filteredSortedByName()
      .filter(tag => !selected.has(tag._id));
  });

  /** Filtered selection. */
  readonly targetList = computed(() => {
    const selected = this.selectedIds();
    return this.tagStore
      .filteredSortedByName()
      .filter(tag => selected.has(tag._id));
  });

  /** Number of selected tags currently hidden by the filter. */
  readonly hiddenSelectionCount = computed(() =>
    Math.max(0, this.selectedIds().size - this.targetList().length)
  );

  /**
   * Adds the moved tags to the store selection.
   *
   * @param {PickListMoveToTargetEvent} event - Contains the moved tags.
   */
  addToSelection(event: PickListMoveToTargetEvent): void {
    this.tagStore.updateSelection(
      addById(this.tagStore.selection(), event.items as Tag[])
    );
  }

  /**
   * Removes the moved tags from the store selection.
   *
   * @param {PickListMoveToSourceEvent} event - Contains the moved tags.
   */
  removeFromSelection(event: PickListMoveToSourceEvent): void {
    this.tagStore.updateSelection(
      removeByIds(
        this.tagStore.selection(),
        (event.items as Tag[]).map(tag => tag._id)
      )
    );
  }

  /**
   * Clears the selection on destroy unless `keepSelection` is set.
   */
  ngOnDestroy(): void {
    if (!this.keepSelection) this.tagStore.clearSelection();
  }
}
